import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, FileText, Calendar, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyses(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'bg-lime-100 text-lime-700';
    if (score >= 60) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Strong';
    if (score >= 60) return 'Moderate';
    if (score >= 40) return 'Weak';
    return 'Poor';
  };

  const filteredAnalyses = analyses.filter(a => 
    (a.job_title?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (a.company?.toLowerCase() || '').includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="history-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl text-slate-900">Analysis History</h1>
            <p className="text-slate-600">{analyses.length} total analyses</p>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by job or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {filteredAnalyses.length > 0 ? (
          <div className="grid gap-4">
            {filteredAnalyses.map((analysis) => (
              <Link key={analysis.id} to={`/app/results/${analysis.id}`}>
                <Card className="bg-white border border-slate-200 hover:shadow-lg transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">
                            {analysis.job_title || 'Untitled Analysis'}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {analysis.company || 'Company not specified'}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {new Date(analysis.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-heading font-bold text-2xl text-slate-900">
                            {analysis.overall_score}%
                          </div>
                          <Badge className={getScoreColor(analysis.overall_score)}>
                            {getScoreLabel(analysis.overall_score)} Match
                          </Badge>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>

                    {analysis.result_summary && (
                      <p className="mt-4 text-sm text-slate-600 line-clamp-2">
                        {analysis.result_summary}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="bg-white border border-slate-200">
            <CardContent className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-900 mb-2">
                {search ? 'No matching analyses found' : 'No analyses yet'}
              </h3>
              <p className="text-slate-500 text-sm mb-4">
                {search 
                  ? 'Try adjusting your search terms' 
                  : 'Start by analyzing your resume against a job description'
                }
              </p>
              {!search && (
                <Link to="/app/analyze">
                  <Button className="bg-slate-900 text-white">
                    Start New Analysis
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
